// University of Denver: public college page (#2652, round 2), official facts
// only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - DU's Common Data Set 2025-2026 (Institutional Research & Analysis; the
//    PDF, DU_Common_Data_Set_2025_2026_v2.pdf, is linked from the IR Common
//    Data Set page used as the source link): C1 applicants, admits and
//    enrollees for Fall 2025 with the residency table, C8 test policy for
//    Fall 2027, C21/C22 plans and the early decision counts.
//  - DU Undergraduate Admission: First Year Applicants (deadlines, plans, the May 1 reply date, Early
//    Decision aid),
//    Application Checklist for First-Year Applicants (deadlines, binding
//    terms, test scores, extra steps for art and music). None of the pages names an entry
//    year; they are the pages posted for the application open now (Fall 2027
//    entry), and the CDS C8 names fall 2027. The admission pages publish no
//    decision dates; the CDS C21/C22 notification dates are given in the
//    early plans note, with the CDS as their source.
//  - DU Financial Aid, Cost of Attendance: the 2026-2027 academic year
//    (fall, winter and spring quarters) for a full-time student living on
//    campus. It matches the CDS G1/G5 items (the CDS also checks the box
//    saying its 2026-2027 costs were not final yet); the page adds a $53
//    average loan fee, which is kept so the rows sum to DU's own total.
//  - College Scorecard API, school id 127060 (net price by family income).
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'University of Denver Common Data Set 2025-2026',
        url: 'https://www.du.edu/ir/data-analytics/institutional-data-sources/cds',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'DU Undergraduate Admission, First Year Applicants',
        url: 'https://www.du.edu/admission-aid/undergraduate/first-year-applicants',
        read: '2026-10-01',
    },
    checklist: {
        label: 'DU Undergraduate Admission, Application Checklist for First-Year Applicants',
        url: 'https://www.du.edu/admission-aid/undergraduate/first-year-applicants/application-checklist',
        read: '2026-10-01',
    },
    cost: {
        label: 'DU Financial Aid, Cost of Attendance',
        url: 'https://www.du.edu/admission-aid/financial-aid-scholarships/undergraduate-financial-aid/price-and-affordability/cost-attendance',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?127060-University-of-Denver',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-denver',
    kbId: 'university_of_denver',
    name: 'University of Denver',
    shortName: 'DU',
    aliases: ['DU', 'Denver'],
    location: 'Denver, CO',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (DU visual style guide); decoration only.
    branding: {
        primary: '#BA0C2F',
        secondary: '#A89968',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_denver_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 16637,
        admitted: 14205,
        enrolled: 1174,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 5623, admitted: 4765 },
            { group: 'Out-of-state', applied: 10033, admitted: 8824 },
            { group: 'International', applied: 981, admitted: 616 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'DU\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision and early action',
        rows: [
            { plan: 'Early decision', applied: 273, admitted: 231 },
        ],
        note: 'The Common Data Set gives one early decision count for the fall 2025 entering class; it does not split Early Decision I from Early Decision II. It lists notification dates of December 15 for Early Decision I and February 20 for Early Decision II. It lists a nonbinding, non-restrictive early action plan, with notification by January 15, but gives no early action counts.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to DU as a whole. Two groups of programs ask for more:',
        rows: [
            { school: 'BFA programs, School of Art & Art History', requirement: 'A portfolio, sent with your other supplemental materials.' },
            { school: 'Music, Lamont School of Music', requirement: 'A supplemental application, which you receive after you submit the Common Application, and an audition or portfolio based on your area of study.' },
        ],
        source: 'checklist',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: null, binding: true },
            { plan: 'Early Action', deadline: 'November 1', decision: null, binding: false },
            { plan: 'Early Decision II', deadline: 'January 15', decision: null, binding: true },
            { plan: 'Regular Decision', deadline: 'January 15', decision: null, binding: false },
        ],
        note: 'DU takes the Common Application. Early Action and Regular Decision are nonbinding: if you apply in either round, you have until May 1 to decide. Students admitted in an Early Decision round receive an additional $5,000 merit scholarship, and top academic performers have their full demonstrated financial need met; athletes receiving athletic aid and music majors may apply Early Decision but are not eligible for these incentives. DU\'s admission pages publish no decision dates.',
        source: 'firstYear',
        binding: 'Both Early Decision plans are binding: you may apply Early Decision to only one school, and if DU admits you, you must withdraw your applications to other colleges. Early Decision applicants also send an Early Decision Agreement Form.',
        bindingSource: 'checklist',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'DU is test-optional: sending SAT or ACT scores is your choice, and if you send them they are read with the rest of your application for admission and merit scholarships. Scores must come straight from the testing agency; scores you enter on the Common Application yourself are not considered. DU superscores, combining your best section scores, and does not consider subject tests or writing sections.',
        source: 'checklist',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for admission but considered if submitted, for fall 2027 applicants, with scores due by February 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 63720 },
            { item: 'Fees', amount: 1584 },
            { item: 'Housing (on campus)', amount: 11283 },
            { item: 'Food', amount: 7532 },
            { item: 'Books, course materials, supplies and equipment', amount: 1000 },
            { item: 'Transportation', amount: 627 },
            { item: 'Personal expenses', amount: 1782 },
            { item: 'Loan fees (average)', amount: 53 },
        ],
        total: 87581,
        note: 'For a full-time undergraduate living on campus for the fall, winter and spring quarters. DU adds $763 for transportation for students who are not Colorado residents. The loan fee is the average federal loan origination fee for students who borrow.',
        source: 'cost',
        netPrice: {
            average: 31961,
            byIncome: [
                { income: '$0 to $30,000', amount: 15042 },
                { income: '$30,001 to $48,000', amount: 15009 },
                { income: '$48,001 to $75,000', amount: 19505 },
                { income: '$75,001 to $110,000', amount: 29756 },
                { income: 'Over $110,000', amount: 46868 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from DU\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
